import { useEffect, useRef, useState } from 'react';

export function IntegerInput({
    value,
    onChange,                  // (value: number | null, isValid: boolean) => void
    onValidate,                // (value: number | null) => boolean
    disabled = false,
    debounceMs = 500,
    className = '',
    callOnChangeWhenInvalid = false,
    resetOnBlurWhenInvalid = true,
    ...rest
}) {
    const [inputValue, setInputValue] = useState(
        value !== null && value !== undefined ? String(value) : ''
    );
    const [dirty, setDirty] = useState(false);
    const [isValid, setIsValid] = useState(true);

    const debounceTimer = useRef(null);

    useEffect(() => {
        if (!dirty) {
            setInputValue(value !== null && value !== undefined ? String(value) : '');
        }
    }, [value, dirty]);

    const parseIntegerInput = raw => {
        if (!/^-?\d+$/.test(raw)) {
            return null;
        }

        const parsed = Number(raw);
        return Number.isInteger(parsed) ? parsed : null;
    };

    const runValidation = parsed => {
        if (typeof onValidate === 'function') {
            return Boolean(onValidate(parsed));
        }
        return parsed !== null;
    };

    const fireOnChange = (parsed, valid) => {
        if (callOnChangeWhenInvalid || valid) {
            onChange(parsed, valid);
        }
    };

    const scheduleChange = raw => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(() => {
            const parsed = parseIntegerInput(raw);
            const valid = runValidation(parsed);

            setIsValid(valid);
            fireOnChange(parsed, valid);
        }, debounceMs);
    };

    const handleChange = e => {
        const raw = e.target.value;

        setInputValue(raw);
        setDirty(true);

        const parsed = parseIntegerInput(raw);
        const valid = runValidation(parsed);
        setIsValid(valid);

        scheduleChange(raw);
    };

    const handleBlur = e => {
        if (!resetOnBlurWhenInvalid) {
            return;
        }

        const parsed = parseIntegerInput(inputValue);
        const valid = runValidation(parsed);

        if (!valid) {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }

            setInputValue(
                value !== null && value !== undefined ? String(value) : ''
            );
            setDirty(false);
            setIsValid(true);
        }

        if (typeof rest.onBlur === 'function') {
            rest.onBlur(e);
        }
    };

    return (
        <input
            type="text"
            disabled={disabled}
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className={[
                'border my-1 py-2 px-3 text-grey-darkest',
                !isValid ? 'border-red-500' : '',
                disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : '',
                className,
            ].join(' ')}
            {...rest}
        />
    );
}